import * as React from 'react';
import styled from 'styled-components';
import { ISignInProvider } from '../../../../data/types';
import { ErrorText } from '../../../shared/form/ErrorMessage';
import { PasswordProviderCard } from './ProviderSelection/PasswordProviderCard';
import { SSOProviderCard } from './ProviderSelection/SSOProviderCard';

export interface IProviderSelectionProps {
  signInProviders: ISignInProvider[];
  selectedProvider: string | null;
  onSelectProvider: (provider: string | null) => void;
  error?: string;
}

export const ProviderSelection = React.memo(
  (props: IProviderSelectionProps): React.ReactElement => {
    // Check if password is available
    const hasPassword = React.useMemo(
      (): boolean =>
        props.signInProviders.some(
          (p: ISignInProvider): boolean => p.type === 'password'
        ),
      [props.signInProviders]
    );

    // Check if SSO providers are available
    const hasSSO = React.useMemo(
      (): boolean =>
        props.signInProviders.some(
          (p: ISignInProvider): boolean => p.type === 'oidc' || p.type === 'saml'
        ),
      [props.signInProviders]
    );

    const handlePasswordClick = React.useCallback((): void => {
      if (props.selectedProvider === 'password') {
        // Don't deselect - user must choose a provider
        return;
      }
      props.onSelectProvider('password');
    }, [props.selectedProvider, props.onSelectProvider]);

    const handleSSOClick = React.useCallback((): void => {
      if (props.selectedProvider === 'sso') {
        // Don't deselect - user must choose a provider
        return;
      }
      props.onSelectProvider('sso');
    }, [props.selectedProvider, props.onSelectProvider]);

    const errorId = 'provider-error';

    let animationIndex = 0;

    return (
      <React.Fragment>
        <h1 id='provider-heading' style={{ margin: '0.25rem 1rem 0.5rem' }}>
          Select Sign-In Method
        </h1>
        <InputContainer role='form' aria-labelledby='provider-heading'>
          <ProviderList role='radiogroup'>
            {hasPassword && (
              <PasswordProviderCard
                selected={props.selectedProvider === 'password'}
                onClick={handlePasswordClick}
                animationDelay={animationIndex++}
              />
            )}
            {hasSSO && (
              <SSOProviderCard
                selected={props.selectedProvider === 'sso'}
                onClick={handleSSOClick}
                animationDelay={animationIndex++}
              />
            )}
          </ProviderList>
          {props.error && (
            <ErrorText id={errorId} role='alert' aria-live='polite'>
              {props.error}
            </ErrorText>
          )}
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<IProviderSelectionProps>,
    nextProps: Readonly<IProviderSelectionProps>
  ): boolean => {
    return (
      prevProps.signInProviders === nextProps.signInProviders &&
      prevProps.selectedProvider === nextProps.selectedProvider &&
      prevProps.error === nextProps.error &&
      prevProps.onSelectProvider === nextProps.onSelectProvider
    );
  }
);

ProviderSelection.displayName = 'ProviderSelection';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 16px 8px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
